import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    CheckCircle2,
    Clock3,
    Loader2,
    Mail,
    MessageSquareText,
    RefreshCw,
    Send,
} from 'lucide-react';
import { publicFormsApi, publicFormFeatureError } from '../services/publicFormsApi';
import { createCommunicationRequestId } from '../utils/communicationRequestId';
import {
    buildMatterPublicFormRequestPayload,
    normalizeMatterPublicFormContext,
    parsePublicFormRequestFieldConfig,
    publicFormRequestIsCompleted,
    publicFormRequestShouldPoll,
} from '../utils/matterPublicForms';
import '../styles/MatterPublicFormField.css';

const POLL_INTERVAL_MS = 30000;

const mergeMatterContext = (current, next) => ({
    ...current,
    ...next,
    form: next.form || current.form,
    recipients: next.recipients.length > 0 ? next.recipients : current.recipients,
    latest_request: next.latest_request || current.latest_request,
    history: next.history.length > 0
        ? [
            ...next.history,
            ...current.history.filter((request) => !next.history.some(
                (nextRequest) => String(nextRequest.id) === String(request.id)
            )),
        ]
        : current.history,
});

const formatTimestamp = (value) => {
    if (!value) return '';
    const normalized = typeof value === 'string'
        && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(?:\.\d+)?$/.test(value)
        ? `${value.replace(' ', 'T')}Z`
        : value;
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString([], {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
};

const requestStatusLabel = (request) => {
    if (!request) return '';
    const status = String(request.status || '').toLowerCase();
    const timestamp = request.sent_at || request.requested_at || request.created_at;
    const appendTimestamp = (label, value = timestamp) => (
        value ? `${label} ${formatTimestamp(value)}` : label
    );
    if (publicFormRequestIsCompleted(request)) {
        return appendTimestamp('Completed', request.completed_at || request.submitted_at);
    }
    if (['failed', 'delivery_failed'].includes(status)) {
        return appendTimestamp('Delivery failed');
    }
    if (status === 'partial_failed') return appendTimestamp('Partially delivered');
    if (['dispatching', 'queued', 'sending'].includes(status)) return appendTimestamp('Sending');
    if (['unknown', 'delivery_unknown', 'confirmation_pending'].includes(status)) {
        return appendTimestamp('Delivery status unknown');
    }
    if (status === 'expired') return appendTimestamp('Link expired');
    if (status === 'revoked') return appendTimestamp('Link revoked');
    if (status === 'cancelled') return appendTimestamp('Cancelled');
    return appendTimestamp('Sent');
};

const channelLabel = (channels) => {
    const values = Array.isArray(channels) ? channels : [];
    if (values.length === 2) return 'Email and text message';
    if (values.includes('email')) return 'Email';
    if (values.includes('sms')) return 'Text message';
    return 'No delivery channel';
};

const MatterPublicFormField = ({
    field,
    leadId,
    caseId,
    groupId = null,
    disabled = false,
    endpoint,
}) => {
    const config = useMemo(() => parsePublicFormRequestFieldConfig(field?.options), [field?.options]);
    const requestPayload = useMemo(() => buildMatterPublicFormRequestPayload({
        field,
        leadId,
        caseId,
        groupId,
    }), [caseId, field, groupId, leadId]);
    const [context, setContext] = useState(() => normalizeMatterPublicFormContext(null));
    const [loading, setLoading] = useState(false);
    const [contextLoaded, setContextLoaded] = useState(false);
    const [sending, setSending] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState('');
    const sendAttemptKeyRef = useRef('');

    const hasMatter = Boolean(requestPayload.lead_id || requestPayload.case_id);
    const configured = Boolean(config.form_id);
    const requestIdentity = JSON.stringify(requestPayload);

    useEffect(() => {
        sendAttemptKeyRef.current = '';
    }, [requestIdentity]);

    const loadContext = useCallback(async ({ quiet = false, signal } = {}) => {
        if (!configured || !hasMatter) return null;
        if (quiet) setRefreshing(true); else setLoading(true);
        try {
            const payload = await publicFormsApi.matterRequestContext({
                public_form_id: requestPayload.public_form_id,
                field_id: requestPayload.field_id,
                lead_id: requestPayload.lead_id,
                case_id: requestPayload.case_id,
                group_id: requestPayload.group_id,
            }, { endpoint, signal });
            const nextContext = normalizeMatterPublicFormContext(payload);
            setContext((current) => quiet ? mergeMatterContext(current, nextContext) : nextContext);
            setContextLoaded(true);
            setError('');
            return nextContext;
        } catch (requestError) {
            if (requestError?.name !== 'AbortError') {
                if (!quiet) setContextLoaded(false);
                setError(publicFormFeatureError(requestError));
            }
            return null;
        } finally {
            if (!signal?.aborted) {
                if (quiet) setRefreshing(false); else setLoading(false);
            }
        }
    }, [configured, endpoint, hasMatter, requestPayload]);

    useEffect(() => {
        const controller = new AbortController();
        setContext(normalizeMatterPublicFormContext(null));
        setContextLoaded(false);
        setError('');
        loadContext({ signal: controller.signal });
        return () => controller.abort();
    }, [loadContext]);

    const latestRequest = context.latest_request;
    const completed = publicFormRequestIsCompleted(latestRequest);
    const latestStatus = String(latestRequest?.status || '').toLowerCase();
    const deliveryInProgress = ['dispatching', 'queued', 'sending'].includes(latestStatus);
    const resendAllowed = !latestRequest || latestRequest.can_resend !== false;
    const latestTone = completed
        ? ' completed'
        : ['failed', 'delivery_failed'].includes(latestStatus)
            ? ' failed'
            : ['partial_failed', 'unknown', 'delivery_unknown', 'confirmation_pending'].includes(latestStatus)
                ? ' warning'
                : '';

    useEffect(() => {
        if (!configured || !hasMatter || !publicFormRequestShouldPoll(latestRequest)) return undefined;
        const interval = window.setInterval(() => loadContext({ quiet: true }), POLL_INTERVAL_MS);
        return () => window.clearInterval(interval);
    }, [completed, configured, hasMatter, latestRequest, loadContext]);

    const handleSend = async () => {
        if (sending || disabled || !configured || !hasMatter || !contextLoaded || deliveryInProgress || !resendAllowed || context.can_send === false) return;
        setSending(true);
        setError('');
        const idempotencyKey = sendAttemptKeyRef.current || createCommunicationRequestId();
        sendAttemptKeyRef.current = idempotencyKey;
        try {
            const payload = await publicFormsApi.sendMatterRequest({
                ...requestPayload,
                idempotency_key: idempotencyKey,
            }, { endpoint });
            sendAttemptKeyRef.current = '';
            const nextContext = normalizeMatterPublicFormContext(payload);
            if (nextContext.latest_request) {
                setContext((current) => mergeMatterContext(current, nextContext));
            }
            await loadContext({ quiet: true });
        } catch (requestError) {
            setError(publicFormFeatureError(requestError));
        } finally {
            setSending(false);
        }
    };

    const recipients = context.recipients
        .map((recipient) => recipient?.name ?? recipient?.full_name ?? recipient?.label)
        .filter(Boolean);
    const unavailableReason = !configured
        ? 'Choose a published public form in this field’s settings.'
        : !hasMatter
            ? 'This form can be sent after the lead or case is created.'
            : deliveryInProgress
                ? 'Delivery is still in progress. Refresh the status before sending again.'
                : !resendAllowed
                    ? 'This request cannot be resent.'
                    : config.default_channels.length === 0
                        ? 'Choose email, text message, or both in this field’s settings.'
                        : context.can_send === false
                            ? context.unavailable_reason || 'No deliverable client email address or mobile number was found.'
                            : '';
    const canSend = !disabled
        && configured
        && hasMatter
        && contextLoaded
        && !deliveryInProgress
        && resendAllowed
        && config.default_channels.length > 0
        && context.can_send !== false;
    const formName = context.form?.name || context.form?.title || context.form?.public_title || '';

    return (
        <div className="matter-public-form-field">
            <div className="matter-public-form-field-main">
                <button
                    type="button"
                    className="action small quaternary matter-public-form-send"
                    onClick={handleSend}
                    disabled={!canSend || sending || loading}
                    title={unavailableReason || (latestRequest ? 'Create and send a new link' : config.button_label)}
                >
                    {sending || loading || deliveryInProgress
                        ? <Loader2 size={16} className="spinner" aria-hidden="true" />
                        : latestRequest
                            ? <RefreshCw size={16} aria-hidden="true" />
                            : <Send size={16} aria-hidden="true" />}
                    {sending || deliveryInProgress ? 'Sending…' : latestRequest ? 'Send again' : config.button_label}
                </button>
                <span className="matter-public-form-channels" aria-label={`Default delivery: ${channelLabel(config.default_channels)}`}>
                    {config.default_channels.includes('email') && <Mail size={15} aria-hidden="true" />}
                    {config.default_channels.includes('sms') && <MessageSquareText size={15} aria-hidden="true" />}
                    <span>{channelLabel(config.default_channels)}</span>
                </span>
                {latestRequest && (
                    <span className={`matter-public-form-latest${latestTone}`}>
                        {completed ? <CheckCircle2 size={15} aria-hidden="true" /> : <Clock3 size={15} aria-hidden="true" />}
                        {requestStatusLabel(latestRequest)}
                    </span>
                )}
                {latestRequest && (
                    <button
                        type="button"
                        className="action-micro quaternary matter-public-form-refresh"
                        onClick={() => loadContext({ quiet: true })}
                        disabled={refreshing}
                        aria-label="Refresh form request status"
                        title="Refresh form request status"
                    >
                        <RefreshCw size={15} className={refreshing ? 'spinner' : ''} aria-hidden="true" />
                    </button>
                )}
            </div>

            {(formName || recipients.length > 0) && (
                <p className="matter-public-form-summary subtext">
                    {formName}{formName && recipients.length > 0 ? ' · ' : ''}
                    {recipients.length > 0 ? `To ${recipients.join(', ')}` : ''}
                </p>
            )}

            {unavailableReason && <p className="matter-public-form-note subtext">{unavailableReason}</p>}
            {error && <p className="matter-public-form-error subtext" role="alert">{error}</p>}
            {error && !contextLoaded && configured && hasMatter && (
                <button
                    type="button"
                    className="action-micro quaternary"
                    onClick={() => loadContext()}
                    disabled={loading}
                >
                    Try again
                </button>
            )}

            {context.history.length > 0 && (
                <details className="matter-public-form-history">
                    <summary>History ({context.history.length})</summary>
                    <ol>
                        {context.history.slice(0, 10).map((request) => (
                            <li key={request.id}>
                                <span>{requestStatusLabel(request)}</span>
                                <small>
                                    {request.recipient_name || 'Client'} · {channelLabel(request.channels)}
                                </small>
                            </li>
                        ))}
                    </ol>
                </details>
            )}
        </div>
    );
};

export { channelLabel, formatTimestamp, requestStatusLabel };
export default MatterPublicFormField;
